import { Injectable, inject } from '@angular/core';
import { Router } from '@angular/router';
import { Subject } from 'rxjs';
import { Config } from 'src/modules/app-template/models/config.model';
import { GuiUtils } from 'src/modules/utils/misc/gui-utils';
import { Utils } from 'src/modules/utils/shared/utils';
import { RestEndpoint } from '../models/rest-endpoint.model';
import { BackendResponse } from '../shared/backend-response.model';
import { LoginUser } from '../shared/login-user.model';

@Injectable({providedIn: "root"})
export class AuthenticationService {

    router = inject(Router);

    allowAnonymous = false;
    userDetails: LoginUser = null;

    onUserChanged = new Subject<void>();

    private accessToken: string = null;

    constructor() {
        GuiUtils.checkSingleton(this);
        this.allowAnonymous = Config.get().allowAnonymous;
    }

    getToken(): string {
        if (this.accessToken == null) {
            let accessToken = localStorage.getItem('accessToken');
            if (!Utils.isNoe(accessToken)) {
                this.accessToken = accessToken;
            }
        }
        return this.accessToken;
    }

    getUserName(): string {
        return this.userDetails?.userName;
    }

    async clearLogin(returnUrl = true): Promise<void> {
        if (this.allowAnonymous) {
            return;
        }

        this.setToken(null);
        this.userDetails = null;
        let url = Utils.stringRemovePrefix(window.location.href, window.location.origin);
        if (url.startsWith("/base/")) {
            return;
        }
        if (url.length <= 1) {
            returnUrl = false;
        }
        await this.router.navigateByUrl("/base/login" + (returnUrl ? "?returnUrl=" + Utils.stringRemovePrefix(url, "/base/login?returnUrl=") : ""));
    }

    isLoggedIn(): boolean {
        return this.allowAnonymous || this.userDetails != null;
    }

    setToken(accessToken: string): void {
        this.accessToken = accessToken;
        localStorage.setItem('accessToken', accessToken || "");
    }

    async login(userName: string, password: string): Promise<number> {
        try {
            let res = await (Config.get().backendV2 ? RestEndpoint.main() : RestEndpoint.main().contentTypeForm()).body({ userName, password }).post().run("api/app/auth/login").get(LoginUser);
            if (!Utils.isNoe(res.access_token)) {
                this.setToken(res.access_token);
                await this.updateUserDetails();
                this.onUserChanged.next();
                return 0;
            }
        }
        catch (ex) {
            let error = BackendResponse.getFromException(ex);
            return error.items == null ? 1 : error.items[0].errorId;
        }
        return 1;
    }

    async logout(): Promise<void> {
        await RestEndpoint.main().run("api/app/auth/logout").getText();
        await this.clearLogin(false);
    }  

    async changePassword(oldPassword: string, newPassword: string): Promise<boolean> {
        return RestEndpoint.main().post().body({oldPassword, newPassword}).run("api/app/auth/changePassword").getBool();
    }

    async updateUserDetails(): Promise<void> {
        if (this.allowAnonymous) {
            this.userDetails = new LoginUser("Anonymous", "", null);
        }
        else {
            try {
                let s = await RestEndpoint.main().run("api/app/auth/getuserdetails").getText();
                if (Utils.isNoe(s)) {
                    await this.clearLogin();
                }
                else {
                    this.userDetails = Utils.fromPlainUnsafe(LoginUser, Utils.fromJson(s));
                    if (Utils.isString(this.userDetails)) {
                        throw new Error("userDetails ist vom Typ string: " + s);
                    }
                }
            }
            catch {
                await this.clearLogin();
            }
        }
    }
}
